import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
    ChartPieIcon,
    BanknotesIcon,
    CreditCardIcon,
    CircleStackIcon,
    ChatBubbleLeftIcon,
    AdjustmentsHorizontalIcon,
    UserCircleIcon,
    UserIcon,
    ArrowsUpDownIcon,
    ArrowRightStartOnRectangleIcon,
    SunIcon,
    MoonIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { SidebarPanelIcon } from '@/components/icons/SidebarPanelIcon';
import { LogoFull } from '@/components/LogoFull';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLayout } from '@/contexts/LayoutContext';

interface SidebarProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
}

interface NavItem {
    label: string;
    path: string;
    icon: typeof ChartPieIcon;
    requiresAdmin?: boolean;
}

const mainNavItems: NavItem[] = [
    { label: 'Net worth', path: '/dashboard', icon: ChartPieIcon },
    { label: 'Assets', path: '/assets', icon: BanknotesIcon },
    { label: 'Debts', path: '/debts', icon: CreditCardIcon },
];

const bottomNavItems: NavItem[] = [
    { label: 'Credits', path: '/credits', icon: CircleStackIcon },
    { label: 'Feedback', path: '/feedback', icon: ChatBubbleLeftIcon },
    { label: 'Admin', path: '/admin', icon: ShieldCheckIcon, requiresAdmin: true },
];

export function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
    const location = useLocation();
    const { isAuthenticated, user, logout } = useAuth();
    const { effectiveTheme, setTheme } = useTheme();
    const { isFullWidth, toggleWidth } = useLayout();

    const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;

    const toggleTheme = () => {
        setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
    };

    const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

    // Filter bottom nav items based on admin status
    const filteredBottomNavItems = bottomNavItems.filter(
        (item) => !item.requiresAdmin || isAdmin
    );

    return (
        <aside
            className={`sticky top-0 z-40 hidden h-screen shrink-0 border-r border-gray-300 bg-white transition-all duration-300 dark:border-gray-700 dark:bg-darker lg:block ${
                isCollapsed ? 'w-16' : 'w-64'
            }`}
        >
            <div className="flex h-full flex-col">
                {/* Logo - matches header height */}
                <div className="flex h-[57px] shrink-0 items-center justify-between border-b border-gray-300 px-4 dark:border-gray-700">
                    {!isCollapsed && (
                        <Link
                            to={isAuthenticated ? '/dashboard' : '/'}
                            className="flex h-12 items-center"
                        >
                            <LogoFull className="h-8 w-auto" />
                        </Link>
                    )}
                    <button
                        onClick={onToggleCollapse}
                        className="shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-primary-light dark:hover:bg-secondary"
                        aria-label={
                            isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                        }
                    >
                        <SidebarPanelIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Main Navigation */}
                <nav
                    aria-label="Main"
                    className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-700 dark:hover:scrollbar-thumb-gray-600 flex-1 space-y-2 overflow-y-auto px-4 py-4"
                >
                    {mainNavItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center rounded-md p-2 transition-colors ${
                                    isCollapsed ? 'justify-center' : 'gap-2'
                                } ${
                                    active
                                        ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-accent dark:text-white dark:hover:bg-secondary dark:hover:text-primary-light'
                                }`}
                                title={isCollapsed ? item.label : undefined}
                            >
                                <Icon
                                    className={`h-6 w-6 shrink-0 ${
                                        active
                                            ? 'text-accent dark:text-primary-light'
                                            : 'text-gray-500 dark:text-primary-light'
                                    }`}
                                />
                                {!isCollapsed && (
                                    <span className="whitespace-nowrap text-base">
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                        );
                    })}

                    {/* Divider - always present but invisible when collapsed */}
                    <div className={`my-2 ${isCollapsed ? 'mx-2' : 'ml-2'} border-b border-gray-300 dark:border-gray-700`} />

                    {filteredBottomNavItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center rounded-md p-2 transition-colors ${
                                    isCollapsed ? 'justify-center' : 'gap-2'
                                } ${
                                    active
                                        ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-accent dark:text-white dark:hover:bg-secondary dark:hover:text-primary-light'
                                }`}
                                title={isCollapsed ? item.label : undefined}
                            >
                                <Icon
                                    className={`h-6 w-6 shrink-0 ${
                                        active
                                            ? 'text-accent dark:text-primary-light'
                                            : 'text-gray-500 dark:text-primary-light'
                                    }`}
                                />
                                {!isCollapsed && (
                                    <span className="whitespace-nowrap text-base">
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Sidebar footer - Layout/Theme toggles + Customize */}
                <div className="shrink-0 border-t border-gray-300 px-4 py-4 dark:border-gray-700 space-y-2">
                    {/* Width toggle button */}
                    <button
                        onClick={toggleWidth}
                        className={`flex w-full items-center rounded-md p-2 transition-colors ${
                            isCollapsed ? 'justify-center' : 'gap-2'
                        } text-gray-700 hover:bg-gray-100 hover:text-accent dark:text-white dark:hover:bg-secondary dark:hover:text-primary-light`}
                        title={isCollapsed ? (isFullWidth ? 'Constrained width' : 'Full width') : undefined}
                    >
                        {isFullWidth ? (
                            <ArrowsPointingInIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                        ) : (
                            <ArrowsPointingOutIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                        )}
                        {!isCollapsed && (
                            <span className="whitespace-nowrap text-base">
                                {isFullWidth ? 'Constrained width' : 'Full width'}
                            </span>
                        )}
                    </button>

                    {/* Theme toggle button */}
                    <button
                        onClick={toggleTheme}
                        className={`flex w-full items-center rounded-md p-2 transition-colors ${
                            isCollapsed ? 'justify-center' : 'gap-2'
                        } text-gray-700 hover:bg-gray-100 hover:text-accent dark:text-white dark:hover:bg-secondary dark:hover:text-primary-light`}
                        title={isCollapsed ? (effectiveTheme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
                    >
                        {effectiveTheme === 'dark' ? (
                            <SunIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                        ) : (
                            <MoonIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                        )}
                        {!isCollapsed && (
                            <span className="whitespace-nowrap text-base">
                                {effectiveTheme === 'dark' ? 'Light mode' : 'Dark mode'}
                            </span>
                        )}
                    </button>

                    {/* Customize link */}
                    <Link
                        to="/settings"
                        className={`flex items-center rounded-md p-2 transition-colors ${
                            isCollapsed ? 'justify-center' : 'gap-2'
                        } ${
                            isActive('/settings')
                                ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-accent dark:text-white dark:hover:bg-secondary dark:hover:text-primary-light'
                        }`}
                        title={isCollapsed ? 'Customize' : undefined}
                    >
                        <AdjustmentsHorizontalIcon className={`h-6 w-6 shrink-0 ${
                            isActive('/settings')
                                ? 'text-accent dark:text-primary-light'
                                : 'text-gray-500 dark:text-primary-light'
                        }`} />
                        {!isCollapsed && (
                            <span className="whitespace-nowrap text-base">Customize</span>
                        )}
                    </Link>
                </div>

                {/* User menu */}
                <div className="shrink-0 overflow-visible border-t border-gray-300 px-4 py-3 dark:border-gray-700">
                    <Menu as="div" className="relative">
                        <Menu.Button
                            className={`flex w-full items-center rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-secondary ${
                                isCollapsed ? 'justify-center' : 'gap-3'
                            }`}
                            title={isCollapsed ? user?.email?.split('@')[0] || 'User menu' : undefined}
                        >
                            <UserCircleIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                            {!isCollapsed && (
                                <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-gray-900 dark:text-white">
                                    {user?.email?.split('@')[0] || 'User'}
                                </span>
                            )}
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute bottom-full left-0 z-1000 mb-2 w-48 origin-bottom-left rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-hidden dark:border-gray-600 dark:bg-secondary">
                                <Menu.Item>
                                    {({ active }) => (
                                        <Link
                                            to="/profile"
                                            className={`${
                                                active ? 'bg-gray-100 dark:bg-accent' : ''
                                            } flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-white`}
                                        >
                                            <UserIcon className="h-4 w-4" />
                                            Account
                                        </Link>
                                    )}
                                </Menu.Item>
                                <Menu.Item>
                                    {({ active }) => (
                                        <Link
                                            to="/data"
                                            className={`${
                                                active ? 'bg-gray-100 dark:bg-accent' : ''
                                            } flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-white`}
                                        >
                                            <ArrowsUpDownIcon className="h-4 w-4" />
                                            Data
                                        </Link>
                                    )}
                                </Menu.Item>
                                {/* Separator */}
                                <div className="my-1 border-t border-gray-200 dark:border-gray-600" />
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={logout}
                                            className={`${
                                                active ? 'bg-gray-100 dark:bg-accent' : ''
                                            } flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-white`}
                                        >
                                            <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                                            Sign out
                                        </button>
                                    )}
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
        </aside>
    );
}
